/** Livro aberto: junta acesso, render, sumário e retomada de posição.
 *
 * Aqui só mora a orquestração — o estado da leitura foi para o `readerStore` e o
 * desenho para o `ReaderContent`. Foi o prop drilling desta view que impedia a
 * barra inferior e o painel direito de existirem.
 */
import { useEffect } from 'react'

import { HighlightsPanel } from '@/features/reader/components/HighlightsPanel'
import { Bookmarks } from '@/features/reader/components/Bookmarks'
import { ReaderContent } from '@/features/reader/components/ReaderContent'
import { ReaderLayout } from '@/features/reader/components/ReaderLayout'
import { useBookAccess } from '@/features/reader/hooks/useBookAccess'
import { useHighlights } from '@/features/reader/hooks/useHighlights'
import { useKeyboardNavigation } from '@/features/reader/hooks/useKeyboardNavigation'
import { useProgress } from '@/features/reader/hooks/useProgress'
import { useReaderPreferences } from '@/features/reader/hooks/useReaderPreferences'
import { useResumePosition } from '@/features/reader/hooks/useResumePosition'
import { useReaderStore } from '@/features/reader/stores/readerStore'
import { sectionForPage } from '@/features/reader/utils/toc'
import { problemCode } from '@/services/api'
import { useAuthStore } from '@/stores/authStore'

const MENSAGENS_DE_BLOQUEIO: Record<string, string> = {
  not_enrolled: 'Você não está matriculado em nenhuma turma.',
  license_expired: 'A licença da sua escola não está vigente.',
  content_not_released: 'Este material ainda não foi liberado pelo seu professor.',
}

interface ReaderViewProps {
  assetId: string
  onClose: () => void
}

export function ReaderView({ assetId, onClose }: ReaderViewProps) {
  const usuario = useAuthStore((s) => s.user)
  const page = useReaderStore((s) => s.page)
  const scrollPct = useReaderStore((s) => s.scrollPct)
  const abrir = useReaderStore((s) => s.abrir)
  const fechar = useReaderStore((s) => s.fechar)
  const irParaPagina = useReaderStore((s) => s.irParaPagina)

  // A URL do `/access` é pré-assinada e o pdf.js a usa a leitura inteira (range
  // requests), não só na abertura — por isso o acesso tem hook próprio, com
  // renovação antes do vencimento.
  const acesso = useBookAccess({ assetId })

  // As preferências já estão pintadas pelo espelho local desde antes do primeiro
  // paint; isto só reconcilia com o servidor (US-03), para a escolha feita no
  // celular valer no laptop.
  useReaderPreferences()

  // Retomada (US-02/N16): espelho local para abrir na hora, servidor para
  // corrigir se ele for mais novo. `abrir()` também zera o resto do estado —
  // sem isso o próximo livro herdaria a página e os painéis do anterior.
  const retomada = useResumePosition({
    bookId: assetId,
    habilitado: acesso.isSuccess,
  })

  useEffect(() => {
    abrir(assetId, retomada.page, retomada.scrollPct)
  }, [assetId, retomada.page, retomada.scrollPct, abrir])

  useEffect(() => () => fechar(), [fechar])

  const destaques = useHighlights(acesso.isSuccess ? assetId : null)

  const total = acesso.access?.page_count ?? undefined
  useKeyboardNavigation({
    onAnterior: () => irParaPagina(page - 1, total),
    onProxima: () => irParaPagina(page + 1, total),
    habilitado: acesso.isSuccess,
  })

  const secao = acesso.access ? sectionForPage(acesso.access.toc, page) : null
  useProgress({
    bookId: acesso.isSuccess ? assetId : null,
    page,
    locator: secao?.locator ?? null,
    scrollPct,
    enabled: acesso.isSuccess,
  })

  if (acesso.isLoading) return <p role="status">Abrindo o livro…</p>

  if (acesso.access && acesso.access.format !== 'pdf') {
    return <section className="reader-blocked" role="alert"><h1>Formato não disponível</h1>
      <p>Esta versão do leitor trabalha exclusivamente com livros PDF.</p>
      <button type="button" onClick={onClose}>Voltar para a biblioteca</button></section>
  }

  if (acesso.isError) {
    const code = problemCode(acesso.error)
    return (
      <div className="reader-blocked" role="alert">
        <p>{MENSAGENS_DE_BLOQUEIO[code ?? ''] ?? 'Não foi possível abrir este material.'}</p>
        <button type="button" onClick={onClose}>
          Voltar para a biblioteca
        </button>
      </div>
    )
  }

  const access = acesso.access!
  return (
    <ReaderLayout
      access={access}
      onClose={onClose}
      ferramentas={<Bookmarks access={access} />}
      painelDireito={
        <HighlightsPanel
          access={access}
          destaques={destaques.destaques}
          onRemover={destaques.remover}
          carregando={destaques.carregando}
          notasHabilitadas
        />
      }
    >
      {destaques.salvando && <p role="status">Salvando marcação…</p>}
      {destaques.erro && <div role="alert"><p>{destaques.erro}</p>
        <button type="button" disabled={destaques.salvando} onClick={destaques.tentarNovamente}>Tentar novamente</button>
      </div>}
      <ReaderContent
        access={access}
        studentName={usuario?.name ?? ''}
        onFalhaDeCarregamento={acesso.renovarPorFalha}
        renovando={acesso.renovando}
        destaques={destaques.destaques}
        {...(destaques.disponivel ? { onCriarDestaque: destaques.criarAsync } : {})}
      />
    </ReaderLayout>
  )
}
